import React, { createContext, ReactNode, useContext, useState } from 'react'
import {
  buildConfig,
  getDefault,
  getEnvironmentFromUrl,
  setEnvironmentBaseConfig
} from '../../services/app.config'
import { AppConfigProviderContext } from './types'
import {
  ApiConfigResponse,
  AppConfig,
  PromotionOverrides
} from '../../domain/config'
import { BranchUrlParameters } from '../../domain/branch'

const AppConfigContextProvider = createContext<AppConfigProviderContext>(
  {} as AppConfigProviderContext
)

const AppConfigProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [configState, setConfigState] = useState<AppConfig>(getDefault())
  const [codeOverride, setCodeOverride] = useState<string | null>(null)

  const setPromotionCode = (code: string) => setCodeOverride(code)

  const setConfig = (data: ApiConfigResponse) => {
    setConfigState(
      buildConfig(setEnvironmentBaseConfig(getEnvironmentFromUrl()), data)
    )
  }

  const configurationLoaded = (): boolean =>
    Object.keys(configState.variables).some(
      (k: keyof ApiConfigResponse) => !!configState.variables[k]
    )

  const getPromotionCode = (): string | null =>
    codeOverride || localStorage.getItem(BranchUrlParameters.PROMO_CODE)

  const getPromotionFromConfig = (code: string): PromotionOverrides | null =>
    (configState.variables.promotionOverrides &&
      configState.variables.promotionOverrides.filter(
        (p) => p._id === code.toLowerCase()
      )[0]) ||
    null

  const getOverride = (): PromotionOverrides | null => {
    const code = getPromotionCode()
    if (code) {
      return getPromotionFromConfig(code)
    }
    return null
  }

  const getOverrideByPromoCode = (code: string): PromotionOverrides | null =>
    getPromotionFromConfig(code)

  return (
    <AppConfigContextProvider.Provider
      value={{
        configuration: configState,
        setConfig,
        getOverride,
        configurationLoaded,
        getOverrideByPromoCode,
        setPromotionCode
      }}
    >
      {children}
    </AppConfigContextProvider.Provider>
  )
}

export const useConfiguration = () => useContext(AppConfigContextProvider)

export default AppConfigProvider
